import {
  KeyboardEvent,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { MoreHorizontal } from "lucide-react";
import { Popover } from "../Popover/Popover";
import styles from "./Menu.module.css";

export interface MenuItem {
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
  variant?: "default" | "danger";
  disabled?: boolean;
}
export interface MenuSeparator {
  separator: true;
}

type Entry = MenuItem | MenuSeparator;

interface Props {
  items: Entry[];
  /**
   * Optional trigger override. If omitted, the component renders a default ⋯
   * kebab button that opens itself on click.
   */
  trigger?: (api: {
    open: boolean;
    toggle: () => void;
    ref: RefObject<HTMLButtonElement>;
  }) => ReactNode;
  /** Optional placement passed through to Popover */
  placement?: "bottom-start" | "bottom-end";
  "aria-label"?: string;
}

function isSeparator(entry: Entry): entry is MenuSeparator {
  return (entry as MenuSeparator).separator === true;
}

export function Menu({
  items,
  trigger,
  placement = "bottom-end",
  "aria-label": ariaLabel,
}: Props) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Build a list of selectable item indices (skip separators / disabled).
  const selectableIndices = items
    .map((it, idx) => ({ it, idx }))
    .filter(({ it }) => !isSeparator(it) && !(it as MenuItem).disabled)
    .map(({ idx }) => idx);

  const [activeIndex, setActiveIndex] = useState<number>(-1);

  const toggle = useCallback(() => setOpen((o) => !o), []);
  const close = useCallback(() => setOpen(false), []);

  // Reset active index on open/close.
  useEffect(() => {
    if (open) {
      setActiveIndex(selectableIndices[0] ?? -1);
    } else {
      setActiveIndex(-1);
    }
    // Intentionally omit selectableIndices from deps (derived from items).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Focus the active item when it changes (and the menu is open).
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const el = itemRefs.current[activeIndex];
    if (el) {
      el.focus();
      // Scroll into view in case of long menus.
      if (typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ block: "nearest" });
      }
    }
  }, [activeIndex, open]);

  function moveActive(direction: 1 | -1) {
    if (selectableIndices.length === 0) return;
    const currentPos = selectableIndices.indexOf(activeIndex);
    let nextPos: number;
    if (currentPos === -1) {
      nextPos = direction === 1 ? 0 : selectableIndices.length - 1;
    } else {
      nextPos =
        (currentPos + direction + selectableIndices.length) %
        selectableIndices.length;
    }
    setActiveIndex(selectableIndices[nextPos]);
  }

  function handleListKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      moveActive(1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      moveActive(-1);
    } else if (e.key === "Escape") {
      e.preventDefault();
      close();
      triggerRef.current?.focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      if (selectableIndices.length > 0) setActiveIndex(selectableIndices[0]);
    } else if (e.key === "End") {
      e.preventDefault();
      if (selectableIndices.length > 0)
        setActiveIndex(selectableIndices[selectableIndices.length - 1]);
    }
  }

  function select(item: MenuItem) {
    if (item.disabled) return;
    item.onSelect();
    close();
    triggerRef.current?.focus();
  }

  const triggerNode = trigger ? (
    trigger({ open, toggle, ref: triggerRef })
  ) : (
    <button
      ref={triggerRef}
      type="button"
      className={styles.kebab}
      aria-haspopup="menu"
      aria-expanded={open}
      aria-label={ariaLabel ?? "More actions"}
      onClick={toggle}
    >
      <MoreHorizontal width={14} height={14} aria-hidden="true" />
    </button>
  );

  return (
    <>
      {triggerNode}
      <Popover
        open={open}
        onClose={close}
        anchorRef={triggerRef}
        placement={placement}
      >
        <div
          ref={listRef}
          role="menu"
          aria-label={ariaLabel}
          className={styles.list}
          onKeyDown={handleListKeyDown}
        >
          {items.map((entry, idx) => {
            if (isSeparator(entry)) {
              return (
                <hr
                  key={`sep-${idx}`}
                  className={styles.sep}
                  aria-hidden="true"
                />
              );
            }
            const item = entry;
            const isActive = idx === activeIndex;
            const isDanger = item.variant === "danger";
            return (
              <button
                key={`item-${idx}-${item.label}`}
                ref={(el) => {
                  itemRefs.current[idx] = el;
                }}
                type="button"
                role="menuitem"
                aria-selected={isActive}
                disabled={item.disabled}
                tabIndex={isActive ? 0 : -1}
                className={`${styles.item} ${isDanger ? styles.danger : ""} ${
                  item.disabled ? styles.disabled : ""
                }`}
                onClick={() => select(item)}
                onMouseEnter={() => {
                  if (!item.disabled) setActiveIndex(idx);
                }}
              >
                {item.icon && <span className={styles.icon}>{item.icon}</span>}
                <span className={styles.label}>{item.label}</span>
              </button>
            );
          })}
        </div>
      </Popover>
    </>
  );
}
